// Registry of preview scenarios for the (preview) screen-simulation harness.
// Add a fixture under ./fixtures and register it here. NOT app code — the
// harness lives entirely under the (preview) route group + this preview/ dir and is
// gated to __DEV__.
//
// Scenarios are ordered as a FLOW: the elimination-flow group walks the
// eliminate → free fortify → place-bonus sequence in order, so the preview
// index reads top-to-bottom as the real player experience.
import type { GameResponse } from '@warcarp/api-types'
import { game as postElimFreeFortify } from './fixtures/postElimFreeFortify'
import { game as midGameElimination } from './fixtures/midGameElimination'
import { game as elimPlusCardBonus } from './fixtures/elimPlusCardBonus'
import { game as cardBonusOnly } from './fixtures/cardBonusOnly'

export interface PreviewScenario {
  id: string
  /** Section header the scenario is filed under in the preview index. */
  group: string
  title: string
  description: string
  /** Slot the preview is "viewed as" (defaults to 1). */
  viewerSlot?: number
  game: GameResponse
}

const ELIM_FLOW = 'Elimination flow · eliminate → fortify → place'
const OTHER = 'Other placement states'

export const SCENARIOS: PreviewScenario[] = [
  {
    id: 'post-elim-free-fortify',
    group: ELIM_FLOW,
    title: 'Step 1 · Free fortify',
    description:
      'You just eliminated a player and lastConquest is still set, so the free-fortify panel shows FIRST — before any bonus placement. The help text ends "…before you place your elimination bonus armies" and the skip button reads "Skip to placement".',
    viewerSlot: 1,
    game: postElimFreeFortify,
  },
  {
    id: 'mid-game-elimination',
    group: ELIM_FLOW,
    title: 'Step 2a · Place bonus (elimination only)',
    description:
      'After the fortify: the interrupt placement step with a +5 elimination bonus and no card turn-in. Subtitle reads "// includes +5 elimination bonus".',
    viewerSlot: 1,
    game: midGameElimination,
  },
  {
    id: 'elim-plus-card-bonus',
    group: ELIM_FLOW,
    title: 'Step 2b · Place bonus (elimination + card)',
    description:
      'Same placement step, but you also turned in a card set: budget 13 = +5 elimination bonus + 8 card bonus. Subtitle reads "// includes +5 elimination bonus, +8 card bonus".',
    viewerSlot: 1,
    game: elimPlusCardBonus,
  },
  {
    id: 'card-bonus-only',
    group: OTHER,
    title: 'Reinforce · card bonus only',
    description:
      'A normal reinforce turn after a card turn-in (+8 card bonus, no elimination). Subtitle reads "// includes +8 card bonus".',
    viewerSlot: 1,
    game: cardBonusOnly,
  },
]

export function findScenario(id: string | undefined): PreviewScenario | undefined {
  return SCENARIOS.find((s) => s.id === id)
}
